import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import '../dashboard.css';
import { useLanguage } from '../../../contexts/LanguageContext';
import CompletionRing from '../sections/CompletionRing';
import DashCard from './DashCard';
import { formatCount } from './format';

export interface TransactionTotals {
  total: number;
  active: number;
  late: number;
  pendingAssignment: number;
  completed: number;
  rate: number;
}

interface TransactionsOverviewCardProps {
  totals: TransactionTotals | null;
  loading: boolean;
}

const TransactionsOverviewCard: React.FC<TransactionsOverviewCardProps> = ({ totals, loading }) => {
  const { isRTL } = useLanguage();
  const value = (n: number | undefined) => (totals ? formatCount(n) : '—');

  const stats = [
    { key: 'total', label: isRTL ? 'إجمالي المعاملات' : 'Total transactions', value: totals?.total },
    { key: 'active', label: isRTL ? 'نشطة' : 'Active', value: totals?.active, className: 'is-brand' },
    {
      key: 'late',
      label: isRTL ? 'متأخرة' : 'Overdue',
      value: totals?.late,
      className: (totals?.late ?? 0) > 0 ? 'is-amber' : undefined,
    },
    { key: 'pending', label: isRTL ? 'بانتظار التعيين' : 'Awaiting assignment', value: totals?.pendingAssignment },
  ];

  return (
    <DashCard
      className="dash-card-narrow"
      title={isRTL ? 'المعاملات' : 'Transactions'}
      caption={isRTL ? 'حالة جميع المعاملات' : 'Status of all transactions'}
      to="/transactions"
    >
      <div className="dash-stats">
        {stats.map((s) => (
          <div key={s.key} className="dash-stat">
            <span className="dash-stat-label">{s.label}</span>
            {loading ? (
              <span className="dash-skel" style={{ height: 22, width: 48 }} />
            ) : (
              <span className={classNames('dash-stat-value', s.className)}>{value(s.value)}</span>
            )}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="dash-ring-block" aria-hidden="true">
          <span className="dash-skel" style={{ width: 128, height: 128, borderRadius: '50%' }} />
        </div>
      ) : !totals ? null : totals.total === 0 ? (
        <div className="dash-empty">
          <span className="dash-empty-tile" aria-hidden="true"><i className="bi bi-inbox" /></span>
          <span className="dash-empty-title">{isRTL ? 'لا توجد معاملات حتى الآن' : 'No transactions yet'}</span>
          <span className="dash-empty-text">
            {isRTL
              ? 'ستظهر هنا إحصائيات الإنجاز بمجرد إنشاء أول معاملة.'
              : 'Completion statistics will appear here once the first transaction is created.'}
          </span>
          <Link to="/transactions/create" className="dash-btn dash-btn-soft">
            <i className="bi bi-plus-lg" aria-hidden="true" />
            {isRTL ? 'إنشاء معاملة' : 'Create transaction'}
          </Link>
        </div>
      ) : (
        <CompletionRing
          percent={totals.rate}
          label={isRTL ? 'مُنجز' : 'Done'}
          legend={[
            { key: 'done', label: isRTL ? 'مكتملة' : 'Completed', value: formatCount(totals.completed), color: 'var(--dash-green)' },
            { key: 'active', label: isRTL ? 'نشطة' : 'Active', value: formatCount(totals.active), color: 'var(--dash-brand)' },
            { key: 'late', label: isRTL ? 'متأخرة' : 'Overdue', value: formatCount(totals.late), color: 'var(--dash-amber)' },
          ]}
        />
      )}
    </DashCard>
  );
};

export default TransactionsOverviewCard;
